import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import { HonestTable, IHonestTableColumn, IHonestTableMenuOption } from 'components/HonestTable/HonestTable'
import { NavBar } from 'components/NavBar/NavBar'
import { useState } from 'react'
import { Flex, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { Head1, BodyText, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { PlanCode } from '../Operations.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { useNavigate } from 'react-router-dom'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import {
    clearResourceBookings,
    getResourceBookings,
    releaseResourceBooking,
    sweepExpiredResourceBookings,
} from 'features/operations/bookingSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Pagination } from 'components/Pagination/Pagination'
import { NoData } from 'components/NoData'
import { IGetAllResourceBookingsSingleResponse } from 'types/api/operationsBookingApi.interface'
import {
    DEMO_BOOKINGS,
    bookingPriorities,
    bookingStatuses,
    getHolderLabel,
    getResourceLabel,
    holderTypes,
    resourceTypeOptions,
    resourceTypes,
} from './bookingData'
import { USE_OPS_MOCKS } from '../Shared/opsControl'
import { DemoBadge } from '../Shared/OpsSharedComponents'

export const Booking = () => {
    const [params, setParams] = useState<any>(getParams({ location, limit: 30 }))
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { data, totalPages } = useSelector((state: RootState) => state.resourceBookings)
    const usingDemoData = !data?.length && USE_OPS_MOCKS
    const bookings = data?.length ? data : usingDemoData ? DEMO_BOOKINGS : []

    const columns: IHonestTableColumn<IGetAllResourceBookingsSingleResponse>[] = [
        {
            id: 'resource',
            header: 'Resource',
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <BodyText weight='600'>{getResourceLabel(item)}</BodyText>
                    <PlanCode>{resourceTypes.find((type) => type.value === item.resourceType)?.label}</PlanCode>
                </Cell>
            ),
        },
        {
            id: 'holder',
            header: 'Holder',
            cell: (item) => (
                <Cell>
                    <BodyText>{getHolderLabel(item)}</BodyText>
                    <PlanCode>{holderTypes.find((type) => type.value === item.holderType)?.label}</PlanCode>
                </Cell>
            ),
        },
        {
            id: 'priority',
            header: 'Priority',
            cell: (item) => <BodyText>{bookingPriorities.find((priority) => priority.value === item.priority)?.label || '-'}</BodyText>,
        },
        {
            id: 'window',
            header: 'Window',
            cell: (item) => (
                <OtherText>
                    {formatDateForDisplay(item.startAt)} → {formatDateForDisplay(item.endAt)}
                </OtherText>
            ),
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => {
                const status = item.isActive ? bookingStatuses[0] : bookingStatuses[1]
                return <StatusPill status={status.tone}>{status.label}</StatusPill>
            },
        },
        {
            id: 'createdAt',
            header: 'Created',
            align: 'right',
            cell: (item) => <OtherText>{formatDateForDisplay(item.createdAt)}</OtherText>,
        },
    ]

    useQueryPagination({ fetchFunctions: [getResourceBookings], cleanUpFunctions: [clearResourceBookings], params })

    const filters: IFilter[] = [
        {
            type: FilterEnum.SELECT,
            name: 'Resource type',
            options: resourceTypeOptions,
            propName: 'resourceType',
            single: true,
        },
    ]

    const handleRelease = (item: IGetAllResourceBookingsSingleResponse) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(releaseResourceBooking({ id: item.id }))
                    dispatch(getResourceBookings(params))
                },
                word: 'release this booking',
            }),
        )
    }

    const handleSweep = () => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(sweepExpiredResourceBookings())
                    dispatch(getResourceBookings(params))
                },
                sentence: 'Release all bookings whose end time has passed? The resources become available for other work. This only affects expired bookings.',
                confirmText: 'Sweep',
            }),
        )
    }

    const getOptions = (item: IGetAllResourceBookingsSingleResponse): IHonestTableMenuOption<IGetAllResourceBookingsSingleResponse>[] => {
        const options: IHonestTableMenuOption<IGetAllResourceBookingsSingleResponse>[] = []

        if (item.isActive) {
            options.push({ label: 'Release', color: tokens.status.danger.fg, onClick: handleRelease })
        }

        return options
    }

    return (
        <>
            <NavBar
                enableTemporaryManualClosing
                filterOptions={filters}
                params={params}
                setParams={setParams}
                limit={30}
                visibleFilterOverflow
            />
            <PageWrapper>
                <Flex between center mb='16'>
                    <Flex center gap='1rem'>
                        <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>Bookings</Head1>
                        {usingDemoData && <DemoBadge />}
                    </Flex>
                    <Flex gap='1rem' center>
                        <Button variant='secondary' title='Release all bookings whose end time has passed' onClick={handleSweep}>Sweep expired</Button>
                        <Button variant='primary' onClick={() => navigate('add')}>Add</Button>
                    </Flex>
                </Flex>
                {bookings.length ? <HonestTable
                    columns={columns}
                    onClick={(item) => navigate(`${item.id}`)}
                    data={bookings}
                    keyField='id'
                    columnToggle
                    menuOptions={(item) => getOptions(item)}
                /> : <Flex column center justcenter gap='1rem'>
                    <NoData text='No resource bookings. Pin a machine or worker to a plan to guarantee availability.' />
                    <Button variant='primary' onClick={() => navigate('add')}>Add booking</Button>
                </Flex>}
            </PageWrapper>
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        </>
    )
}
